import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectLanguage, setSelectedCategory, setSelectedBrand } from '../redux/cartSlice';
import { translations } from '../utils/translations';
import './Footer.css';
import NewsletterSignup from './NewsletterSignup';

const footerTranslations = {
  en: {
    columnTitle: "My Account",
    myAccount: "My Account",
    wishList: "Wish List",
    orderHistory: "Order History",
    rmaForm: "RMA Form",
    register: "Register"
  },
  nl: {
    columnTitle: "Mijn Account",
    myAccount: "Mijn Account",
    wishList: "Verlanglijst",
    orderHistory: "Bestelgeschiedenis",
    rmaForm: "RMA Formulier",
    register: "Registreren"
  },
  es: {
    columnTitle: "Mi Cuenta",
    myAccount: "Mi Cuenta",
    wishList: "Lista de Deseos",
    orderHistory: "Historial de Pedidos",
    rmaForm: "Formulario RMA",
    register: "Registrarse"
  },
  fr: {
    columnTitle: "Mon Compte",
    myAccount: "Mon Compte",
    wishList: "Liste de Souhaits",
    orderHistory: "Historique des Commandes",
    rmaForm: "Formulaire RMA",
    register: "S'inscrire"
  },
  de: {
    columnTitle: "Mein Konto",
    myAccount: "Mein Konto",
    wishList: "Wunschzettel",
    orderHistory: "Bestellverlauf",
    rmaForm: "RMA-Formular",
    register: "Registrieren"
  },
  it: {
    columnTitle: "Il Mio Account",
    myAccount: "Il Mio Account",
    wishList: "Lista dei Desideri",
    orderHistory: "Cronologia Ordini",
    rmaForm: "Modulo RMA",
    register: "Registrati"
  },
  pt: {
    columnTitle: "Minha Conta",
    myAccount: "Minha Conta",
    wishList: "Lista de Desejos",
    orderHistory: "Histórico de Pedidos",
    rmaForm: "Formulário RMA",
    register: "Registrar"
  },
  el: {
    columnTitle: "Ο Λογαριασμός Μου",
    myAccount: "Ο Λογαριασμός Μου",
    wishList: "Λίστα Επιθυμιών",
    orderHistory: "Ιστορικό Παραγγελιών",
    rmaForm: "Φόρμα RMA",
    register: "Εγγραφή"
  }
};

const Footer = () => {
  const selectedLang = useSelector(selectLanguage) || { code: 'en' };
  const t = translations[selectedLang.code || 'en'] || translations.en;
  const ft = footerTranslations[selectedLang.code || 'en'] || footerTranslations.en;
  const dispatch = useDispatch();

  const navigateTo = (e, path) => {
    e.preventDefault();
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleCategoryLinkClick = (e, catName) => {
    e.preventDefault();

    if (window.location.pathname !== '/' && window.location.pathname !== '') {
      window.history.pushState({}, '', '/');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }

    dispatch(setSelectedCategory(catName));
    dispatch(setSelectedBrand('All'));

    const tryScroll = () => {
      const element = document.getElementById('explore-products');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return true;
      }
      return false;
    };

    if (!tryScroll()) {
      let attempts = 0;
      const timer = setInterval(() => {
        attempts++;
        if (tryScroll() || attempts >= 20) {
          clearInterval(timer);
        }
      }, 50);
    }
  };

  const handleSectionNavigation = (e, sectionId) => {
    e.preventDefault();

    if (window.location.pathname !== '/' && window.location.pathname !== '') {
      window.history.pushState({}, '', '/');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }

    const tryScroll = () => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return true;
      }
      return false;
    };

    if (!tryScroll()) {
      let attempts = 0;
      const timer = setInterval(() => {
        attempts++;
        if (tryScroll() || attempts >= 20) {
          clearInterval(timer);
        }
      }, 50);
    }
  };

  return (
    <footer className="footer-container">
      <div className="footer-content-wrapper">
        <div className="footer-grid">
          {/* Column 1: Brand Logo & Support Info */}
          <div className="footer-column brand-column">
            {/* Store Logo */}
            <div className="footer-logo-wrapper">
              <img src="/images/logo.png" alt="Smart Telecom Store" className="footer-logo-img" />
            </div>

            {/* Hotline call block */}
            <div className="footer-hotline-block">
              <div className="hotline-icon-wrapper">
                {/* Yellow headphone icon matching screenshot */}
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#be1e2d" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                  <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
                </svg>
              </div>
              <div className="hotline-text-wrapper">
                <span className="hotline-sub">{t.footerQuestions || 'Got Questions ? Call us 24/7!'}</span>
                <a href="tel:+31687748358" className="hotline-main-number">+31-687748358</a>
              </div>
            </div>

            {/* Contact Address & Email */}
            <div className="footer-contact-details" style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13.5px', color: '#a0aec0', marginTop: '10px' }}>
              <h4 className="contact-title" style={{ fontSize: '15px', fontWeight: '700', color: '#ffffff', marginBottom: '8px', textTransform: 'uppercase' }}>{t.contactDetails || 'Contact Details'}</h4>

              <p className="contact-company" style={{ margin: 0, fontWeight: '600', color: '#ffffff' }}>Recent Inspiration</p>
              <p className="contact-address" style={{ margin: 0 }}>Zuylesteinstraat 21, 1333XZ Almere</p>
              <a href="tel:+31687748358" className="contact-phone" style={{ color: '#a0aec0', textDecoration: 'none' }}>Tel / WhatsApp: +31-687748358</a>
              <a href="mailto:smarttelecom.nl@gmail.com" className="contact-email" style={{ color: '#a0aec0', textDecoration: 'none' }}>smarttelecom.nl@gmail.com</a>
              <div className="contact-biz-info" style={{ marginTop: '8px', fontSize: '13.5px', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '8px', color: '#718096', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                <span>KvK: 42091099</span>
                <span style={{ color: 'rgba(255,255,255,0.15)' }}>|</span>
                <span>VAT: NL005488135B34</span>
              </div>
            </div>

            {/* Social Icons row */}
            <div className="footer-social-icons">
              <a href="#facebook" aria-label="Facebook">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                </svg>
              </a>
              <a href="#whatsapp" aria-label="WhatsApp">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                </svg>
              </a>
              <a href="#pinterest" aria-label="Pinterest">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="8" x2="12" y2="16" />
                  <line x1="8" y1="12" x2="16" y2="12" />
                </svg>
              </a>
              <a href="#linkedin" aria-label="LinkedIn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                  <rect x="2" y="9" width="4" height="12" />
                  <circle cx="4" cy="4" r="2" />
                </svg>
              </a>
              <a href="#instagram" aria-label="Instagram">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                </svg>
              </a>
              <a href="#youtube" aria-label="YouTube">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" />
                  <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" />
                </svg>
              </a>
              <a href="#rss" aria-label="RSS Feed">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 11a9 9 0 0 1 9 9" />
                  <path d="M4 4a16 16 0 0 1 16 16" />
                  <circle cx="5" cy="19" r="1" />
                </svg>
              </a>
            </div>
          </div>

          <div className="footer-column">
            <h4 className="footer-title">{t.categories || 'Categories'}</h4>
            <ul className="footer-links-list">
              <li><a href="#explore-products" onClick={(e) => handleCategoryLinkClick(e, 'Mobile Phone')}>{t.categoryMobilePhone || 'Mobile Phone'}</a></li>
              <li><a href="#explore-products" onClick={(e) => handleCategoryLinkClick(e, 'Accessories')}>{t.categoryAccessories || 'Accessories'}</a></li>
              <li><a href="#explore-products" onClick={(e) => handleCategoryLinkClick(e, 'Gadget')}>{t.categoryGadget || 'Gadget'}</a></li>
              <li><a href="#explore-products" onClick={(e) => handleCategoryLinkClick(e, 'Services')}>{t.categoryService || 'Service'}</a></li>
              <li><a href="#explore-products" onClick={(e) => handleCategoryLinkClick(e, 'Parts')}>{t.categoryParts || 'Parts'}</a></li>
              <li><a href="#explore-products" onClick={(e) => handleCategoryLinkClick(e, 'Computers')}>{t.categoryComputers || 'Computers'}</a></li>
              <li><a href="#explore-products" onClick={(e) => handleCategoryLinkClick(e, 'Offers')}>{t.categoryOffers || 'Offers'}</a></li>
              <li><a href="#explore-products" onClick={(e) => handleCategoryLinkClick(e, 'Used Phone')}>{t.categoryUsedPhone || 'Used Phone'}</a></li>
              <li><a href="#explore-products" onClick={(e) => handleCategoryLinkClick(e, 'Electronics')}>{t.categoryElectronics || 'Electronics'}</a></li>
            </ul>
          </div>

          {/* Column 3: Useful Links */}
          <div className="footer-column">
            <h4 className="footer-title">{t.usefulLinks || 'Useful Links'}</h4>
            <ul className="footer-links-list">
              <li><a href="/about-us" onClick={(e) => navigateTo(e, '/about-us')}>{t.aboutUs || 'About Us'}</a></li>
              <li><a href="/contact-us" onClick={(e) => navigateTo(e, '/contact-us')}>{t.contactUs || 'Contact Us'}</a></li>
              <li><a href="/warranty-policy" onClick={(e) => navigateTo(e, '/warranty-policy')}>{t.warrantyPolicy || 'Warranty Policy'}</a></li>
              <li><a href="/blog" onClick={(e) => navigateTo(e, '/blog')}>Blog</a></li>
              <li><a href="#explore-products" onClick={(e) => handleCategoryLinkClick(e, 'All')}>{t.products || 'Products'}</a></li>
              <li><a href="#new-arrivals" onClick={(e) => handleSectionNavigation(e, 'new-arrivals')}>{t.newArrival || 'New Arrivals'}</a></li>
              <li><a href="/register" onClick={(e) => navigateTo(e, '/register')}>{ft.register}</a></li>
              <li><a href="/login" onClick={(e) => navigateTo(e, '/login')}>{t.signIn || 'Sign In'}</a></li>
            </ul>
            <NewsletterSignup source="footer" />
          </div>

          {/* Column 4: Account Links */}
          <div className="footer-column">
            <h4 className="footer-title">{ft.columnTitle}</h4>
            <ul className="footer-links-list">
              <li><a href="#account" onClick={(e) => {
                e.preventDefault();
                const session = localStorage.getItem('user_session');
                window.history.pushState({}, '', session ? '/profile' : '/login');
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}>{ft.myAccount}</a></li>
              <li><a href="#wishlist" onClick={(e) => {
                e.preventDefault();
                const session = localStorage.getItem('user_session');
                window.history.pushState({}, '', session ? '/wish-list' : '/login');
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}>{ft.wishList}</a></li>
              <li><a href="#orders" onClick={(e) => {
                e.preventDefault();
                const session = localStorage.getItem('user_session');
                window.history.pushState({}, '', session ? '/order-history' : '/login');
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}>{ft.orderHistory}</a></li>
              <li><a href="#rma" onClick={(e) => {
                e.preventDefault();
                console.log('RMA Form footer link clicked. Dispatching open_rma_modal event.');
                window.dispatchEvent(new Event('open_rma_modal'));
              }}>{ft.rmaForm}</a></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar: Copyright & Payment Logo */}
      <div className="footer-bottom-bar">
        <div className="footer-bottom-inner">
          <p className="copyright-text">
            &copy; <strong>Smart Telecom Store</strong> - {t.allRightsReserved || 'All Rights Reserved'}
          </p>
          <div className="payment-icons-wrapper">
            <img src="/images/payment-icons.webp" alt="Supported Payments (Discover, Mastercard, PayPal, Skrill, Visa)" className="payment-icons-img" />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

